/**
 * Client-side cascade — JS port of run_cascade() in scripts/nightly_schedule.py.
 *
 * When a milestone's finish date moves, dependents shift forward by the same rules
 * the nightly scheduler uses:
 *   - new EST = upstream finish + 1 working day (weekends skipped)
 *   - preserve the calendar window width (LFT - EST), so new LFT = new EST + width
 *   - STOP at any dependent that is Date Pinned (a real, locked date; posted ones are passed as pinned)
 *
 * The Python job stays the nightly authority; this is its interactive twin so the
 * Gantt can preview re-flows instantly. Pure function — no I/O, no mutation of inputs.
 */

const DAY_MS = 24 * 60 * 60 * 1000
// Only a real, locked date stops the cascade (Date Pinned). Fixed/Scheduled types (Public Hearing,
// Community Meeting, Field Report) used to stop it on their own, so a date guessed at project setup never
// moved; since 2026-09-24 they flow until their real date is set, which locks them. Callers pass posted
// milestones as datePinned so they never move either.
const STOP_BEHAVIORS = new Set([])

/** Parse 'YYYY-MM-DD' to a UTC-noon Date (DST-safe day math). Returns null if blank/invalid. */
export function parseDate(s) {
  if (!s) return null
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(s))
  if (!m) return null
  return new Date(Date.UTC(+m[1], +m[2] - 1, +m[3], 12, 0, 0))
}

/** Format a Date back to 'YYYY-MM-DD'. */
export function formatDate(d) {
  if (!d) return ''
  return d.getUTCFullYear() + '-' +
    String(d.getUTCMonth() + 1).padStart(2, '0') + '-' +
    String(d.getUTCDate()).padStart(2, '0')
}

export function addDays(d, n) {
  return new Date(d.getTime() + n * DAY_MS)
}

/** Whole-day difference (b - a), calendar days. */
export function dayDiff(a, b) {
  return Math.round((b.getTime() - a.getTime()) / DAY_MS)
}

/** Advance to the next weekday if the date lands on Sat/Sun. */
export function nextWorkday(d) {
  let r = d
  while (r.getUTCDay() === 0 || r.getUTCDay() === 6) r = addDays(r, 1)
  return r
}

/**
 * Compute cascade updates from a single changed milestone.
 *
 * @param {Object} args
 * @param {string} args.rootId            milestone that changed
 * @param {string} args.rootFinish        the changed milestone's new finish ('YYYY-MM-DD')
 * @param {Array}  args.milestones        [{ id, earliestStart, latestFinish, datePinned, behavior }]
 * @param {Array}  args.dependencies      [{ milestoneId, predecessorId }]
 * @returns {Map<string,{earliestStart:string, latestFinish:string}>} id -> new dates
 */
export function computeCascade({ rootId, rootFinish, milestones, dependencies }) {
  const byId = new Map(milestones.map(m => [m.id, m]))

  // predecessor -> [dependent ids], and dependent -> [predecessor ids]
  const dependentsMap = new Map()
  const predsOf = new Map()
  for (const dep of dependencies) {
    if (!dep.predecessorId || !dep.milestoneId) continue
    if (!dependentsMap.has(dep.predecessorId)) dependentsMap.set(dep.predecessorId, [])
    dependentsMap.get(dep.predecessorId).push(dep.milestoneId)
    if (!predsOf.has(dep.milestoneId)) predsOf.set(dep.milestoneId, [])
    predsOf.get(dep.milestoneId).push(dep.predecessorId)
  }

  const updates = new Map()
  const rootDate = parseDate(rootFinish)
  // A milestone with several predecessors waits for ALL of them: it starts the day after the
  // LATEST predecessor finish (Matt, 2026-09-23). Finishes already moved in this cascade win over
  // the stored ones.
  const finishOf = (id) => {
    if (id === rootId) return rootDate
    const u = updates.get(id)
    if (u) return parseDate(u.latestFinish)
    const m = byId.get(id)
    return m ? parseDate(m.latestFinish) : null
  }
  // A node is walked again when its finish changes again (diamond-shaped chains); bounded so a
  // predecessor loop can never hang the Gantt.
  const walked = new Map()
  let steps = 0
  const queue = [[rootId, rootDate]]

  while (queue.length && steps++ < 5000) {
    const [currentId, upstreamDate] = queue.shift()
    if (!upstreamDate) continue
    if (walked.get(currentId) === formatDate(upstreamDate)) continue
    walked.set(currentId, formatDate(upstreamDate))

    for (const depId of (dependentsMap.get(currentId) || [])) {
      const dep = byId.get(depId)
      if (!dep) continue
      if (dep.datePinned) continue                          // STOP (pinned)
      if (STOP_BEHAVIORS.has(dep.behavior)) continue        // STOP (Fixed/Scheduled)

      const curEst = parseDate(dep.earliestStart)
      const curLft = parseDate(dep.latestFinish)
      if (!curLft) continue

      let latestPred = upstreamDate
      for (const p of (predsOf.get(depId) || [])) {
        const f = finishOf(p)
        if (f && f > latestPred) latestPred = f
      }
      const newEst = nextWorkday(addDays(latestPred, 1))
      // Preserve the calendar window width (LFT - EST), mirroring the nightly job.
      const width = curEst ? dayDiff(curEst, curLft) : 0
      const proposed = addDays(newEst, Math.max(width, 0))

      // Already tight — no change, but keep walking downstream from current LFT.
      if (curEst && dayDiff(newEst, curEst) === 0 && dayDiff(proposed, curLft) === 0) {
        queue.push([depId, curLft])
        continue
      }

      updates.set(depId, { earliestStart: formatDate(newEst), latestFinish: formatDate(proposed) })
      queue.push([depId, proposed])
    }
  }

  return updates
}
